// filepath: frontend/src/lib/plans.ts
// Shared plan configuration for frontend UX (pricing, episodes, credits)
// Keep in sync with backend PlanConstants.

export type LegacyPlanId = 'tier_one' | 'tier_two' | 'creator' | 'tier_three'
export type WeeklyPlanId = 'sip' | 'brew' | 'daily'
// Backwards-compat alias for code that imported SeriesPlanId.
export type SeriesPlanId = WeeklyPlanId
export type PlanId = 'free' | LegacyPlanId | WeeklyPlanId

/** Selectable episode durations for weekly plans (each has its own Stripe SKU). */
export const WEEKLY_EPISODE_DURATIONS = [10, 20, 30] as const
export type EpisodeDurationMinutes = typeof WEEKLY_EPISODE_DURATIONS[number]
export const DEFAULT_EPISODE_DURATION_MINUTES: EpisodeDurationMinutes = 30
export const isValidEpisodeDuration = (value: unknown): value is EpisodeDurationMinutes =>
  typeof value === 'number' && (WEEKLY_EPISODE_DURATIONS as readonly number[]).includes(value)

export interface PlanInfo {
  id: PlanId
  name: string
  price: number
  priceLabel: string
  /** Per-billing-cycle price label (e.g. "$2.99/wk" for weekly plans, "$15.99/mo" for legacy). */
  pricePeriodLabel: string
  minutesPerMonth: number
  /** For weekly plans only: episodes the user gets per cycle. */
  episodesPerCycle?: number
  /** Episode duration range for weekly plans. */
  minEpisodeMinutes?: number
  maxEpisodeMinutes?: number
  cadenceLabel?: string
  allowanceLabel?: string
  billingFamily?: 'legacy' | 'weekly'
  billingInterval?: 'week' | 'month'
  includesResearch?: boolean
  priorityQueue?: boolean
  recommended?: boolean
  /** Weekly plans: price per selectable episode duration. */
  pricesByDuration?: Record<EpisodeDurationMinutes, number>
}

export const LEGACY_PLAN_IDS: LegacyPlanId[] = ['tier_one', 'tier_two', 'creator', 'tier_three']
export const WEEKLY_PLAN_IDS: WeeklyPlanId[] = ['sip', 'brew', 'daily']
// Backwards-compat alias.
export const SERIES_PLAN_IDS: WeeklyPlanId[] = WEEKLY_PLAN_IDS

export const isLegacyPlan = (planId?: string | null): planId is LegacyPlanId =>
  !!planId && LEGACY_PLAN_IDS.includes(planId as LegacyPlanId)
export const isWeeklyPlan = (planId?: string | null): planId is WeeklyPlanId =>
  !!planId && WEEKLY_PLAN_IDS.includes(planId as WeeklyPlanId)
// Backwards-compat alias.
export const isSeriesPlan = isWeeklyPlan
export const isKnownPlan = (planId?: string | null): planId is PlanId =>
  planId === 'free' || isLegacyPlan(planId) || isWeeklyPlan(planId)

const MIN_EP = 10
const MAX_EP = 30

export const PLANS: Record<PlanId, PlanInfo> = {
  free: {
    id: 'free',
    name: 'Free',
    price: 0,
    priceLabel: '$0',
    pricePeriodLabel: 'Free',
    minutesPerMonth: 10,
    episodesPerCycle: 1,
    minEpisodeMinutes: MIN_EP,
    maxEpisodeMinutes: MIN_EP,
    allowanceLabel: '1 episode (up to 10 min)',
    cadenceLabel: 'One-time',
    billingFamily: 'weekly',
    billingInterval: 'week',
  },
  tier_one: {
    id: 'tier_one',
    name: 'Starter',
    price: 15.99,
    priceLabel: '$15.99',
    pricePeriodLabel: '$15.99/mo',
    minutesPerMonth: 100,
    allowanceLabel: '100 minutes/month',
    billingFamily: 'legacy',
    billingInterval: 'month',
    includesResearch: true,
  },
  tier_two: {
    id: 'tier_two',
    name: 'Pro',
    price: 34.99,
    priceLabel: '$34.99',
    pricePeriodLabel: '$34.99/mo',
    minutesPerMonth: 300,
    allowanceLabel: '300 minutes/month',
    billingFamily: 'legacy',
    billingInterval: 'month',
    includesResearch: true,
  },
  creator: {
    id: 'creator',
    name: 'Creator',
    price: 79.99,
    priceLabel: '$79.99',
    pricePeriodLabel: '$79.99/mo',
    minutesPerMonth: 800,
    allowanceLabel: '800 minutes/month',
    billingFamily: 'legacy',
    billingInterval: 'month',
    includesResearch: true,
  },
  tier_three: {
    id: 'tier_three',
    name: 'Studio',
    price: 134.99,
    priceLabel: '$134.99',
    pricePeriodLabel: '$134.99/mo',
    minutesPerMonth: 1500,
    allowanceLabel: '1500 minutes/month',
    billingFamily: 'legacy',
    billingInterval: 'month',
    includesResearch: true,
  },
  sip: {
    id: 'sip',
    name: 'Sip',
    price: 2.99,
    priceLabel: '$2.99',
    pricePeriodLabel: '$2.99/wk',
    minutesPerMonth: MAX_EP * 1 * 4,
    episodesPerCycle: 1,
    minEpisodeMinutes: MIN_EP,
    maxEpisodeMinutes: MAX_EP,
    cadenceLabel: '1 episode / week',
    allowanceLabel: '1 episode per week',
    billingFamily: 'weekly',
    billingInterval: 'week',
    includesResearch: true,
    pricesByDuration: { 10: 1.99, 20: 2.49, 30: 2.99 },
  },
  brew: {
    id: 'brew',
    name: 'Brew',
    price: 7.99,
    priceLabel: '$7.99',
    pricePeriodLabel: '$7.99/wk',
    minutesPerMonth: MAX_EP * 3 * 4,
    episodesPerCycle: 3,
    minEpisodeMinutes: MIN_EP,
    maxEpisodeMinutes: MAX_EP,
    cadenceLabel: '3 episodes / week',
    allowanceLabel: '3 episodes per week',
    billingFamily: 'weekly',
    billingInterval: 'week',
    includesResearch: true,
    recommended: true,
    pricesByDuration: { 10: 4.99, 20: 5.99, 30: 7.99 },
  },
  daily: {
    id: 'daily',
    name: 'Daily',
    price: 18.99,
    priceLabel: '$18.99',
    pricePeriodLabel: '$18.99/wk',
    minutesPerMonth: MAX_EP * 7 * 4,
    episodesPerCycle: 7,
    minEpisodeMinutes: MIN_EP,
    maxEpisodeMinutes: MAX_EP,
    cadenceLabel: '1 episode every day',
    allowanceLabel: '7 episodes per week',
    billingFamily: 'weekly',
    billingInterval: 'week',
    includesResearch: true,
    priorityQueue: true,
    pricesByDuration: { 10: 9.99, 20: 13.99, 30: 18.99 },
  },
}

// Identifiers used by the in-app paywall/upgrade dialog. Keep in sync with `paywallId` values
// passed to <UpgradeDialog />.
export type PaywallId =
  | 'series_resume'
  | 'download_episode'
  | 'private_podcasts'
  | 'duration_longer_episodes'
  | (string & {})

// Cheapest plan that unlocks the feature behind a given paywall.
export const getMinPlanForPaywall = (_paywallId: PaywallId): PlanId => {
  // All current weekly plans support every feature (research, downloads, full 10-30 min length).
  // Sip is the cheapest entry point.
  return 'sip'
}

export const getDiscountComparedToStarter = (planId: PlanId): number | null => {
  if (planId === 'free' || planId === 'tier_one') return null

  const plan = PLANS[planId]
  const starter = PLANS.tier_one

  const planPricePerMinute = plan.price / plan.minutesPerMonth
  const starterPricePerMinute = starter.price / starter.minutesPerMonth

  if (planPricePerMinute >= starterPricePerMinute) return null

  const discount = (1 - planPricePerMinute / starterPricePerMinute) * 100
  return Math.round(discount)
}

export const creditsForMinutes = (minutes: number) => Math.round((minutes * 60) / 10)

export const minutesForCredits = (credits: number) => {
  const secondsPerCredit = 10
  return Math.round((credits * secondsPerCredit) / 60)
}

export const getPlanCreditsCap = (planId: PlanId) => creditsForMinutes(PLANS[planId].minutesPerMonth)

export const getPlanMinutesCap = (planId: PlanId) => PLANS[planId].minutesPerMonth

export const suggestTierForCredits = (estimatedCredits: number, currentPlan?: PlanId): PlanId => {
  const order: PlanId[] = isWeeklyPlan(currentPlan)
    ? ['free', ...WEEKLY_PLAN_IDS]
    : ['free', ...LEGACY_PLAN_IDS]
  const currentIndex = currentPlan ? order.indexOf(currentPlan) : -1

  for (let i = 0; i < order.length; i++) {
    const id = order[i]
    if (i > currentIndex && estimatedCredits <= getPlanCreditsCap(id)) {
      return id
    }
  }
  return isWeeklyPlan(currentPlan) ? 'daily' : 'tier_three'
}

/** Returns the price for a weekly plan at the given episode duration, or the plan's base price for non-weekly plans. */
export const getWeeklyPlanPrice = (planId: PlanId, duration: EpisodeDurationMinutes): number => {
  const plan = PLANS[planId]
  return plan.pricesByDuration?.[duration] ?? plan.price
}

/** Formats a weekly plan price for display (e.g. "$2.49"). */
export const getWeeklyPlanPriceLabel = (planId: PlanId, duration: EpisodeDurationMinutes): string => {
  const price = getWeeklyPlanPrice(planId, duration)
  return Number.isInteger(price) ? `$${price.toFixed(0)}` : `$${price.toFixed(2)}`
}
